import { CircularProgress, Grid, Theme, makeStyles } from '@material-ui/core';
import React, { ReactElement, ReactNode } from 'react';
import LegalLinks from '../../elements/LegalLinks';
import TypographyI18N from '../../elements/TypographyI18N';
import { BrandedCardContentProps, defaultProps as defaultContentProps } from './BrandedCardContent';
import { BrandedCardHeaderProps, defaultProps as defaultHeaderProps } from './BrandedCardHeader';
import BrandedCardHelmet from './BrandedCardHelmet';
import BrandedCardLogo from './BrandedCardLogo';

const useStyles = makeStyles<Theme, BrandedCardProps>(() => ({
    // The outermost root container
    root: {
        justifyContent: 'center',
        alignItems: 'stretch',
        marginTop: 20,
        width: 'calc(100% - 30px)',
        margin: '0 auto',
    },

    // Container that organizes the content of the card vertically and controls the height of the card
    innerContainer: {
        alignContent: 'flex-start',
        alignItems: 'center',
        backgroundColor: 'white',
        borderRadius: 10,
        justifyContent: 'center',
        maxWidth: 450,
        minHeight: (props) => props.minHeight,
        // Preserve the content wrapper used by the legacy loader.
        '& .loadedContent': {
            width: '100%',
        },
    },
    loader: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
    },
    loaderText: {
        position: 'relative',
        top: 200,
    },
}));

export type BrandedCardVariant = 'default' | 'large-header';

export type BrandedCardProps = {
    isLoading?: boolean;
    loaderText?: string;
    maxWidth?: number;
    minHeight?: number;
    showLogo?: boolean;
    children: ReactNode | ReactNode[];
    variant?: BrandedCardVariant;
};

const defaultProps: Partial<BrandedCardProps> = {
    isLoading: false,
    maxWidth: 450,
    minHeight: 500,
    variant: 'default',
};

const LARGER_HEADER_DELTA = 20; // Make the header 20px taller and the content 20px shorter

const BrandedCard = (props: BrandedCardProps) => {
    const actualProps = { ...defaultProps, ...props };
    const { showLogo, isLoading, variant } = actualProps;
    const classes = useStyles(actualProps);

    const children = React.Children.toArray(actualProps.children);
    let header = children.find((child: any) => child.type.displayName === 'BrandedCardHeader');
    let content = children.find((child: any) => child.type.displayName === 'BrandedCardContent');
    const actions = children.find((child: any) => child.type.displayName === 'BrandedCardActions');

    if (variant === 'large-header') {
        const headerHeight = (header as ReactElement<BrandedCardHeaderProps>).props.height ?? defaultHeaderProps.height;
        header = React.cloneElement(header as ReactElement, {
            height: headerHeight + LARGER_HEADER_DELTA,
        });

        const contentHeight =
            (content as ReactElement<BrandedCardContentProps>).props.height ?? defaultContentProps.height;
        content = React.cloneElement(content as ReactElement, {
            height: contentHeight - LARGER_HEADER_DELTA,
        });
    }

    const logo = showLogo ? <BrandedCardLogo /> : '';

    const loaderText = isLoading ? (
        <TypographyI18N className={classes.loaderText}>{actualProps.loaderText}</TypographyI18N>
    ) : null;
    const cardContent = isLoading ? (
        <div className={classes.loader}>
            <CircularProgress />
        </div>
    ) : (
        <div className='loadedContent'>
            {content}
            {actions}
        </div>
    );

    return (
        <Grid container className={classes.root} direction='row'>
            <Grid container className={classes.innerContainer} item xs={12} direction='row'>
                <BrandedCardHelmet />
                {logo}
                {header}
                {cardContent}
                {loaderText}
            </Grid>
        </Grid>
    );
};

export default BrandedCard;
